// apps/patient/src/components/messages/MessageBubble.tsx

import { formatDistanceToNow } from 'date-fns';
import type { LucideIcon } from 'lucide-react';
import {
  Check,
  CheckCheck,
  AlertCircle,
  Clock,
  Paperclip,
  Reply,
  Download,
  FileText,
  Image as ImageIcon,
  File,
  Pen
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import ImageMarkupTool from './markup/ImageMarkupTool';
import { patientApiService as apiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import PrivateImage, { getSignedImageUrl } from '../ui/PrivateImage';

export default function MessageBubble({
  message,
  isOwnMessage,
  onReply,
  threadId,
  isPatient = true
}) {
  const [imageError, setImageError] = useState<Record<string | number, boolean>>({});
  const { patient } = usePatient();

  const [markupImage, setMarkupImage] = useState<{
    url: string;
    name: string;
    attachmentId: string | number;
    mimeType: string;
  } | null>(null);

  const [savingMarkup, setSavingMarkup] = useState(false);

  const isImage = (mimeType?: string) => !!mimeType && mimeType.startsWith('image/');

  const getDeliveryIcon = () => {
    if (message.delivery_status === 'failed') {
      return <AlertCircle className="h-3 w-3 text-red-500" />;
    }

    if (message.read_receipts?.length > 0) {
      return <CheckCheck className="h-3 w-3 text-blue-500" />;
    }

    if (message.delivery_status === 'delivered') {
      return <CheckCheck className="h-3 w-3 text-gray-400" />;
    }

    if (message.delivery_status === 'sent') {
      return <Check className="h-3 w-3 text-gray-400" />;
    }

    return <Clock className="h-3 w-3 text-gray-400" />;
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60); // Use getTime() for proper arithmetic

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }

    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getFileIcon = (mimeType?: string): LucideIcon => {
    if (mimeType?.startsWith('image/')) return ImageIcon;
    if (mimeType?.includes('pdf')) return FileText;
    return File;
  };

  const handleDownload = (attachment) => {
    if (attachment.url || attachment.file_url) {
      window.open(attachment.url || attachment.file_url, '_blank');
    }
  };


  const handleMarkup = async (attachment: any) => {
    try {
      // Get signed URL for the markup tool
      const imageUrl = await getSignedImageUrl(attachment);

      setMarkupImage({
        url: imageUrl,
        name: attachment.filename || attachment.file_name || attachment.name || 'image',
        attachmentId: attachment.id,
        mimeType: attachment.mime_type || 'image/png',
      });
    } catch (error) {
      console.error('Failed to load image for markup:', error);
      alert('Failed to load image for editing. Please try again.');
    }
  };

  const handleSaveMarkup = async (file: File, annotationData: string) => {
    setSavingMarkup(true);
    try {
      if (!threadId) throw new Error('Missing threadId');

      const messageData = {
        body: `Annotated: ${markupImage?.name ?? 'image'}`,
        sender_id: patient?.id,
        sender_name: `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim(),
        sender_type: 'patient',          // IMPORTANT: patient, not provider
        thread_id: threadId,
        parent_message_id: message.id,
        is_reply: true,
        contains_attachments: true,
      };

      // 1) Create the reply message
      const messageResponse = await apiService.sendMessage(threadId, messageData);

      // 2) Upload the annotated image
      if (messageResponse?.id) {
        const uploadResult = await apiService.uploadMessageAttachment(file, messageResponse.id, threadId);

        if (uploadResult) {
          const formattedAttachment = {
            id: uploadResult.id,
            filename: uploadResult.filename || uploadResult.file_name || uploadResult.file_data?.name,
            file_name: uploadResult.file_name || uploadResult.file_data?.name || uploadResult.filename,
            name: uploadResult.file_name || uploadResult.file_data?.name || uploadResult.filename,
            size: uploadResult.file_size,
            mime_type: uploadResult.mime_type,
            url: uploadResult.file_url || uploadResult.file_data?.url,
            file_url: uploadResult.file_url || uploadResult.file_data?.url,
          };

          await apiService.updateMessage(messageResponse.id, {
            attachments: [formattedAttachment],
            attachment_ids: [uploadResult.id],
          });
        }

        // Store annotations for potential re-edit
        localStorage.setItem(`annotation_${messageResponse.id}`, annotationData);
      }

      // Close tool
      setMarkupImage(null);

      // Trigger an immediate refresh of the currently open thread
      const w = window as any;
      if (typeof w.refreshMessages === 'function') {
        w.refreshMessages();
      } else {
        window.dispatchEvent(new CustomEvent('refreshMessages'));
      }
    } catch (err) {
      console.error('Error saving annotated image:', err);
      alert('Failed to save annotated image. Please try again.');
    } finally {
      setSavingMarkup(false);
    }
  };

  return (
    <div className={cn(
      "flex gap-2 group relative",
      isOwnMessage ? "justify-end" : "justify-start"
    )}>
      {/* Avatar for provider messages */}
      {!isOwnMessage && (
        <div className="flex-shrink-0 mt-1">
          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-xs font-medium text-gray-600">
              {message.sender_name?.charAt(0)?.toUpperCase() || 'P'}
            </span>
          </div>
        </div>
      )}

      <div className={cn(
        "max-w-[70%] flex flex-col gap-1",
        isOwnMessage && "items-end"
      )}>
        {/* Sender name (for provider messages) */}
        {!isOwnMessage && (
          <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
            <span className="font-medium">{message.sender_name || 'Provider'}</span>
            {message.sender_type === 'provider' && (
              <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                Provider
              </span>
            )}
          </div>
        )}

        {/* Message bubble */}
        <div className={cn(
          "relative group",
          isOwnMessage ? "items-end" : "items-start"
        )}>
          <div className={cn(
            "px-4 py-2 rounded-2xl",
            isOwnMessage
              ? "bg-primary text-white rounded-br-sm"
              : "bg-gray-100 text-gray-900 rounded-bl-sm"
          )}>
            {/* Reply indicator */}
            {message.parent_message_id && (
              <div className={cn(
                "text-xs mb-2 pb-2 border-b",
                isOwnMessage ? "border-white/20" : "border-gray-200"
              )}>
                <Reply className="h-3 w-3 inline mr-1" />
                Replying to message
              </div>
            )}

            {/* Message body */}
            <p className="whitespace-pre-wrap break-words">{message.body}</p>

            {/* Attachments */}
            {message.attachments?.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.attachments.map((attachment, idx) => {
                  const Icon = getFileIcon(attachment.mime_type);
                  const fileName =
                    attachment.filename || attachment.file_name || attachment.name || 'File';

                  return (
                    <div key={attachment.id || idx} className="flex items-center gap-2">
                      {isImage(attachment.mime_type) && !imageError[attachment.id] ? (
                        <div className="relative group/image">
                          <PrivateImage
                            attachment={attachment}
                            className="max-w-full max-h-48 object-cover rounded-lg cursor-pointer"
                            onClick={async () => {
                              try {
                                // For opening in new tab, we need the signed URL
                                const url = await getSignedImageUrl(attachment);
                                window.open(url, '_blank');
                              } catch (error) {
                                console.error('Failed to open image:', error);
                              }
                            }}
                            fallback={
                              // Show as file attachment if image fails to load
                              <button
                                onClick={() => handleDownload(attachment)}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 rounded-lg',
                                  isOwnMessage ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-gray-50'
                                )}
                              >
                                <Icon className="h-4 w-4" />
                                <span className="text-sm truncate max-w-[200px]">{fileName}</span>
                                <Download className="h-3 w-3 ml-1" />
                              </button>
                            }
                          />
                          {/* Hover controls */}
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/image:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleMarkup(attachment)}
                              className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white"
                              title="Annotate Image"
                            >
                              <Pen className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDownload(attachment)}
                              className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleDownload(attachment)}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg',
                            isOwnMessage ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-gray-50'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm truncate max-w-[200px]">{fileName}</span>
                          <Download className="h-3 w-3 ml-1" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Timestamp and status */}
            <div className={cn(
              "flex items-center gap-2 mt-2 text-xs",
              isOwnMessage ? "text-white/70" : "text-gray-500"
            )}>
              <span>{formatMessageTime(message.created_at)}</span>
              {isOwnMessage && getDeliveryIcon()}
              {message.edited && <span>(edited)</span>}
            </div>
          </div>

          {/* Reply action (hover) */}
          {onReply && (
            <button
              onClick={onReply}
              className={cn(
                "absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity",
                "p-1 rounded bg-white shadow-sm border",
                isOwnMessage ? "-left-8" : "-right-8"
              )}
            >
              <Reply className="h-3 w-3 text-gray-600" />
            </button>
          )}
        </div>
      </div>
      {markupImage && (
        <ImageMarkupTool
          imageUrl={markupImage.url}
          imageName={markupImage.name}
          onSave={handleSaveMarkup}
          onClose={() => setMarkupImage(null)}
          existingAnnotations={localStorage.getItem(`annotation_${message.id}`)}
        />
      )}
    </div>
  );
}